/**
 * Drivers Step - Real driver entry (name, permits, vehicle assignment)
 * Replaces abstract driver counter with actual driver data capture.
 */

import React, { useState } from 'react';
import { Users, Award, Plus, Trash2, Check, User, Phone, Briefcase } from 'lucide-react';

const CONTRACT_TYPES = [
  { id: 'active', label: 'CDI' },
  { id: 'temporary', label: 'CDD' },
  { id: 'interim', label: 'Intérim' },
];

const PERMIT_TYPES = [
  { id: 'B', label: 'B', description: 'Voiture' },
  { id: 'C', label: 'C', description: 'PL > 3.5T' },
  { id: 'CE', label: 'CE', description: 'Super lourd' },
  { id: 'D', label: 'D', description: 'Bus / Car' },
];

const CERTIFICATIONS = [
  { id: 'caces', label: 'CACES', description: 'Chariots / nacelles' },
  { id: 'adr', label: 'ADR', description: 'Matières dangereuses' },
  { id: 'fimo', label: 'FIMO/FCO', description: 'Formation marchandises' },
];

export function DriversStep({ data, updateData, onNext, onSkip }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDriver, setNewDriver] = useState({
    firstName: '',
    lastName: '',
    permits: ['B'],
    phone: '',
    contractType: 'active',
  });

  const drivers = data.drivers || [];
  const vehicles = data.vehicles || [];

  const addDriver = () => {
    if (!newDriver.firstName.trim() || !newDriver.lastName.trim()) return;

    const updated = [...drivers, {
      firstName: newDriver.firstName.trim(),
      lastName: newDriver.lastName.trim().toUpperCase(),
      permits: newDriver.permits,
      phone: newDriver.phone,
      contractType: newDriver.contractType,
      assignedVehicleIndex: null,
    }];
    updateData({ drivers: updated });
    setNewDriver({ firstName: '', lastName: '', permits: ['B'], phone: '', contractType: 'active' });
    setShowAddForm(false);
  };

  const removeDriver = (index) => {
    updateData({ drivers: drivers.filter((_, i) => i !== index) });
  };

  const updateDriverAssignment = (driverIndex, vehicleIndex) => {
    const updated = [...drivers];
    updated[driverIndex] = {
      ...updated[driverIndex],
      assignedVehicleIndex: vehicleIndex === '' ? null : parseInt(vehicleIndex),
    };
    updateData({ drivers: updated });
  };

  const togglePermit = (permitId) => {
    setNewDriver(prev => ({
      ...prev,
      permits: prev.permits.includes(permitId)
        ? prev.permits.filter(p => p !== permitId)
        : [...prev.permits, permitId],
    }));
  };

  const toggleCertification = (certId) => {
    const current = data.certifications || [];
    const updated = current.includes(certId)
      ? current.filter(c => c !== certId)
      : [...current, certId];
    updateData({ certifications: updated });
  };

  // Build vehicle options for assignment dropdown
  const vehicleOptions = vehicles
    .map((v, i) => ({
      index: i,
      label: `${v.license_plate || '???'} - ${v.make || ''} ${v.model || ''}`.trim(),
    }))
    .filter(v => v.label.length > 5);

  const completedDrivers = drivers.filter(d => d.firstName && d.lastName).length;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl">
          <Users className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          Et côté conducteurs ?
        </h2>
        <p className="text-gray-500">
          Ajoutez vos conducteurs avec leur nom et type de permis
        </p>
      </div>

      {/* Driver list */}
      {drivers.length > 0 && (
        <div className="space-y-2">
          {drivers.map((driver, index) => {
            const isComplete = driver.firstName && driver.lastName;
            return (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl shadow-sm"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-50' : 'bg-amber-50'}`}>
                  <User className={`w-4 h-4 ${isComplete ? 'text-green-500' : 'text-amber-500'}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900 font-medium truncate">
                      {driver.firstName} {driver.lastName}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isComplete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {isComplete ? 'Complet' : 'À compléter'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      driver.contractType === 'active' ? 'bg-green-100 text-green-700' :
                      driver.contractType === 'temporary' ? 'bg-amber-100 text-amber-700' :
                      driver.contractType === 'interim' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {CONTRACT_TYPES.find(ct => ct.id === driver.contractType)?.label || 'CDI'}
                    </span>
                    <span className="text-gray-400 text-xs">
                      Permis: {(driver.permits || ['B']).join(', ')}
                    </span>
                    {driver.phone && (
                      <span className="text-gray-400 text-xs">| {driver.phone}</span>
                    )}
                  </div>
                </div>

                {/* Vehicle assignment dropdown */}
                {vehicleOptions.length > 0 && (
                  <select
                    value={driver.assignedVehicleIndex ?? ''}
                    onChange={(e) => updateDriverAssignment(index, e.target.value)}
                    className="w-40 px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="">À affecter</option>
                    {vehicleOptions.map(v => (
                      <option key={v.index} value={v.index}>{v.label}</option>
                    ))}
                  </select>
                )}

                <button
                  onClick={() => removeDriver(index)}
                  className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add driver form */}
      {showAddForm ? (
        <div className="p-4 bg-white border border-gray-300 rounded-xl space-y-4 shadow-sm animate-fade-in">
          <h4 className="text-gray-900 font-medium">Nouveau conducteur</h4>

          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={newDriver.firstName}
              onChange={(e) => setNewDriver(prev => ({ ...prev, firstName: e.target.value }))}
              placeholder="Prénom"
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              autoFocus
            />
            <input
              type="text"
              value={newDriver.lastName}
              onChange={(e) => setNewDriver(prev => ({ ...prev, lastName: e.target.value }))}
              placeholder="Nom"
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          {/* Phone (optional) */}
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="tel"
              value={newDriver.phone}
              onChange={(e) => setNewDriver(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="Téléphone (optionnel)"
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          {/* Contract type */}
          <div className="space-y-2">
            <label className="text-gray-600 text-sm font-medium flex items-center gap-1.5">
              <Briefcase className="w-4 h-4" />
              Type de contrat
            </label>
            <div className="flex gap-2">
              {CONTRACT_TYPES.map((ct) => (
                <button
                  key={ct.id}
                  onClick={() => setNewDriver(prev => ({ ...prev, contractType: ct.id }))}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-all
                    ${newDriver.contractType === ct.id
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }
                  `}
                >
                  {ct.label}
                </button>
              ))}
            </div>
          </div>

          {/* Permit types */}
          <div className="space-y-2">
            <label className="text-gray-600 text-sm font-medium">Type de permis</label>
            <div className="flex gap-2">
              {PERMIT_TYPES.map((permit) => (
                <button
                  key={permit.id}
                  onClick={() => togglePermit(permit.id)}
                  className={`
                    px-3 py-2 rounded-lg text-sm font-medium transition-all
                    ${newDriver.permits.includes(permit.id)
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }
                  `}
                  title={permit.description}
                >
                  {permit.label}
                </button>
              ))}
            </div>
          </div>

          {/* Form actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowAddForm(false)}
              className="flex-1 px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={addDriver}
              disabled={!newDriver.firstName.trim() || !newDriver.lastName.trim()}
              className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" />
              Ajouter
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-400 hover:text-gray-700 hover:border-gray-400 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Ajouter un conducteur</span>
        </button>
      )}

      {/* Summary */}
      {drivers.length > 0 && (
        <div className="text-center">
          <span className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-700 font-medium">
            {completedDrivers} conducteur{completedDrivers > 1 ? 's' : ''} ajouté{completedDrivers > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Certifications section */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2 text-amber-600">
          <Award className="w-5 h-5" />
          <h3 className="font-medium">Certifications présentes dans l'équipe</h3>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {CERTIFICATIONS.map((cert) => {
            const isSelected = (data.certifications || []).includes(cert.id);
            return (
              <button
                key={cert.id}
                onClick={() => toggleCertification(cert.id)}
                className={`
                  flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left
                  ${isSelected
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                `}
              >
                <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${isSelected ? 'bg-amber-500' : 'bg-gray-200'}`}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                <div>
                  <span className="text-gray-900 font-medium block text-sm">{cert.label}</span>
                  <span className="text-gray-400 text-xs">{cert.description}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Skip hint */}
      <div className="text-center">
        <button
          onClick={onSkip}
          className="text-gray-400 hover:text-gray-600 text-sm underline transition-colors"
        >
          Compléter plus tard
        </button>
      </div>
    </div>
  );
}

export default DriversStep;
